import ListHeader from 'components/ListHeader'
import React from 'react'
import { RouteChildrenProps } from 'react-router'
import { observer } from 'mobx-react'
import styles from './DocumentRoot.sass'
import DocumentRootViewModel from './DocumentRootViewModel'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import {
  StickyViewHeader,
  StickyViewHeaderMain,
  StickyViewHeaderSpacer,
} from 'components/StickyViewHeader'
import PageTip from 'components/PageTip'
import DocumentsListContainer, { DocumentsList } from './DocumentsList'
import { useActivation } from 'hooks/mobx-hooks'
import { useDropzone } from 'hooks/use-dropzone'
import ContentLoading from 'components/ContentLoading'
import ContentError from 'components/ContentError'
import DocumentsEmptyState from './DocumentsEmptyState'
import DocumentsViewHeading from './DocumentsViewHeading'
import Button from 'components/Button'
import FileButton from 'components/FileButton'
import { useDocumentTitle } from 'hooks/document-title'
import UploadBar from './UploadBar'

interface Props extends RouteChildrenProps {
  vm: DocumentRootViewModel
}

export default observer(function DocumentRoot({ vm }: Props) {
  useDocumentTitle('Documents Center')
  const scrollRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(scrollRef)
  useActivation(vm)

  function onEnter() {
    if (!vm.canUploadFiles) {
      return
    }
    if (vm.uploadBarVM.hidden || vm.uploadBarVM.resolved) {
      vm.uploadBarVM.setDragging()
    }
  }

  function onLeave() {
    if (!vm.uploadBarVM.uploading) {
      vm.uploadBarVM.setHidden()
    }
  }

  function onDrop(files: FileList) {
    if (!vm.uploadBarVM.uploading) {
      vm.uploadFiles(files)
    }
  }

  const [, dropZoneRef] = useDropzone<HTMLDivElement>(onDrop, onEnter, onLeave)

  return (
    <div ref={scrollRef} className={styles.root}>
      <div ref={dropZoneRef} className={styles.dragAndDropOverlay}>
        <StickyViewHeader scrollOffset={scrollOffset}>
          <StickyViewHeaderMain>
            <DocumentsViewHeading vm={vm} />
          </StickyViewHeaderMain>
          <StickyViewHeaderSpacer />
        </StickyViewHeader>

        <div className={styles.split}>
          <div className={styles.content}>
            <DocumentsListContainer
              header={
                <ListHeader
                  count={vm.hasDocuments ? vm.workspaceDocs.length : undefined}
                >
                  Documents
                </ListHeader>
              }
            >
              {vm.fetchDocs.match({
                pending: () => <ContentLoading />,
                rejected: (err) => <ContentError error={err} />,
                resolved: () => {
                  return (
                    <>
                      {vm.hasDocuments ? (
                        <DocumentsList
                          vm={vm.documentsListVM}
                          canUploadFiles={
                            vm.isWorkspaceSelected && vm.canUploadFiles
                          }
                          onQueueFilesUpload={vm.onQueueFilesUpload}
                          scrollProvider={vm.getScrollProvider}
                          onViewDocumentDetails={vm.viewDocumentDetails}
                        />
                      ) : (
                        <DocumentsEmptyState
                          canUploadFiles={vm.canUploadFiles}
                          onQueueFilesUpload={vm.onQueueFilesUpload}
                          currentWorkspaceEmpty={vm.isWorkspaceEmpty}
                          currentWorkspace={vm.currentWorkspace}
                          onSelectCurrentWorkspace={
                            vm.selectCurrentWorkspaceForFiltering
                          }
                        />
                      )}
                      {vm.workspaceForFiltering && (
                        <UploadBar
                          targetWorkspace={vm.workspaceForFiltering}
                          vm={vm.uploadBarVM}
                        />
                      )}
                    </>
                  )
                },
              })}
            </DocumentsListContainer>
          </div>
          <div className={styles.side}>
            {vm.canUploadFiles &&
              (vm.isWorkspaceSelected ? (
                <PageTip
                  title="Want to upload multiple documents fast?"
                  illustrationUrl={require('assets/images/img_upload-docs.svg')}
                  button={
                    <FileButton
                      multiple={true}
                      onChange={vm.onQueueFilesUpload}
                      data-test="upload-document-input-classic"
                    >
                      <Button type="primary">Browse Files</Button>
                    </FileButton>
                  }
                >
                  Simply drag and drop anywhere on this screen to upload. Or,
                  click the button below to lookup files
                </PageTip>
              ) : (
                <PageTip
                  title="Select a workspace to upload documents"
                  illustrationUrl={require('assets/images/img_upload-docs.svg')}
                  button={
                    <Button
                      onClick={vm.selectCurrentWorkspaceForFiltering}
                      type="primary"
                    >
                      Switch to {vm.currentWorkspace.name}
                    </Button>
                  }
                >
                  You're viewing documents in all your workspaces. Select a
                  workspace or switch bellow to upload more
                </PageTip>
              ))}
          </div>
        </div>
      </div>
    </div>
  )
})
